import { cn } from '@r/utils/utils.ts';
import { Button } from '@ui/button.tsx';
import { Input } from '@ui/input.tsx';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { usePostForgotPassword } from '@r/api/generated/client.ts';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
const APP_ID = import.meta.env.VITE_APP_ID;

export const ResetPasswordForm = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const resetSchema = z.object({
    email: z.string().min(1, t('form.fields.required')).email(t('form.field.invalid'))
  });

  const form = useForm<z.infer<typeof resetSchema>>({
    resolver: zodResolver(resetSchema),
    defaultValues: {
      email: ''
    }
  });

  const resetPasswordMutation = usePostForgotPassword({
    request: { headers: { 'App-Id': APP_ID } },
    mutation: {
      onSuccess: (_data, variables) => {
        queryClient.setQueryData(['reset_password'], { email: variables.data.email });
      }
    }
  });

  function onSubmit(data: z.infer<typeof resetSchema>) {
    resetPasswordMutation.mutate({ data: { email: data.email } });
  }

  return (
    <div className={cn('grid gap-6')}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <FormField
            control={form.control}
            name="email"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('auth.email.label')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('auth.email.placeholder')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button className="w-full" type="submit" disabled={resetPasswordMutation.isPending}>
            {resetPasswordMutation.isPending ? (
              <>
                <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                Envoyer ma demande
              </>
            ) : (
              'Envoyer ma demande'
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
};
